import { useState, useRef, useEffect } from 'react';
import { useTheme, type Theme } from '../hooks';
import styles from './ThemeSelector.module.css';

const THEME_OPTIONS: { value: Theme; label: string; icon: 'sun' | 'moon' | 'contrast' | 'citrus' | 'editorial' }[] = [
  { value: 'light', label: 'Warm', icon: 'sun' },
  { value: 'dark', label: 'Dark', icon: 'moon' },
  { value: 'mono-light', label: 'Mono', icon: 'contrast' },
  { value: 'citrus-light', label: 'Citrus', icon: 'citrus' },
  { value: 'citrus-dark', label: 'Citrus Dark', icon: 'citrus' },
  { value: 'editorial', label: 'Editorial', icon: 'editorial' },
];

function ThemeIcon({ type, className }: { type: 'sun' | 'moon' | 'contrast' | 'citrus' | 'editorial'; className?: string }) {
  switch (type) {
    case 'sun':
      return (
        <svg className={className} width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="8" cy="8" r="3" fill="currentColor"/>
          <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.05 3.05l1.414 1.414M11.536 11.536l1.414 1.414M3.05 12.95l1.414-1.414M11.536 4.464l1.414-1.414" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
        </svg>
      );
    case 'moon':
      return (
        <svg className={className} width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 10.5A6 6 0 015.5 2a6 6 0 108.5 8.5z" fill="currentColor"/>
        </svg>
      );
    case 'contrast':
      return (
        <svg className={className} width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M8 2v12a6 6 0 000-12z" fill="currentColor"/>
        </svg>
      );
    case 'citrus':
      return (
        <svg className={className} width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M8 4v8M5 6l6 4M5 10l6-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      );
    case 'editorial':
      return (
        <svg className={className} width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="2" y="2" width="5" height="5" stroke="currentColor" strokeWidth="1.5"/>
          <rect x="9" y="2" width="5" height="5" stroke="currentColor" strokeWidth="1.5"/>
          <rect x="2" y="9" width="5" height="5" stroke="currentColor" strokeWidth="1.5"/>
          <rect x="9" y="9" width="5" height="5" stroke="currentColor" strokeWidth="1.5"/>
        </svg>
      );
  }
}

export function ThemeSelector() {
  const { theme, setTheme, isDark } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentOption = THEME_OPTIONS.find(opt => opt.value === theme) || THEME_OPTIONS[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div className={styles.container} ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={styles.trigger}
        aria-label="Change theme"
        title={`Theme: ${currentOption.label}`}
      >
        <ThemeIcon type={isDark ? 'moon' : currentOption.icon} className={styles.icon} />
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.dropdownHeader}>Theme</div>
          {THEME_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`${styles.option} ${option.value === theme ? styles.active : ''}`}
              onClick={() => {
                setTheme(option.value);
                setIsOpen(false);
              }}
            >
              <ThemeIcon type={option.icon} className={styles.optionIcon} />
              <span>{option.label}</span>
              {option.value === theme && (
                <svg className={styles.checkIcon} width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 7L6 10L11 4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
